import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Check, 
  Package, 
  Apple, 
  ShoppingCart,
  Trash2,
  Edit,
  Save,
  X
} from 'lucide-react';
import { theme } from '../../styles/theme';

interface GroceryItem {
  id: string;
  name: string;
  amount: number;
  unit: string;
  category: string;
  is_checked: boolean;
  priority: 'low' | 'medium' | 'high';
  notes?: string;
}

interface GroceryListPlannerProps {
  className?: string;
}

const GroceryListPlanner: React.FC<GroceryListPlannerProps> = ({ className = '' }) => {
  const [items, setItems] = useState<GroceryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<GroceryItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    amount: 1,
    unit: 'piece',
    category: 'Produce',
    priority: 'medium' as const,
    notes: ''
  });

  // Mock data
  useEffect(() => {
    const mockItems: GroceryItem[] = [
      {
        id: '1',
        name: 'Bananas',
        amount: 6,
        unit: 'pieces',
        category: 'Produce',
        is_checked: false,
        priority: 'high'
      },
      {
        id: '2',
        name: 'Chicken Breast',
        amount: 2,
        unit: 'lbs',
        category: 'Meat',
        is_checked: true,
        priority: 'medium'
      },
      {
        id: '3',
        name: 'Brown Rice',
        amount: 1,
        unit: 'bag',
        category: 'Grains',
        is_checked: false,
        priority: 'low'
      },
      {
        id: '4',
        name: 'Spinach',
        amount: 1,
        unit: 'bunch',
        category: 'Produce',
        is_checked: false,
        priority: 'high'
      },
      {
        id: '5',
        name: 'Greek Yogurt',
        amount: 2,
        unit: 'containers',
        category: 'Dairy',
        is_checked: false,
        priority: 'medium'
      }
    ];
    setItems(mockItems);
    setFilteredItems(mockItems);
  }, []);

  useEffect(() => {
    let filtered = items;
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    
    setFilteredItems(filtered);
  }, [items, searchTerm, selectedCategory]);

  const toggleItemChecked = (id: string) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, is_checked: !item.is_checked } : item
    ));
  };

  const addItem = () => {
    if (newItem.name.trim()) {
      const item: GroceryItem = {
        id: Date.now().toString(),
        name: newItem.name.trim(),
        amount: newItem.amount,
        unit: newItem.unit,
        category: newItem.category,
        is_checked: false,
        priority: newItem.priority,
        notes: newItem.notes
      };
      
      setItems(prev => [...prev, item]);
      setNewItem({
        name: '',
        amount: 1,
        unit: 'piece',
        category: 'Produce',
        priority: 'medium',
        notes: ''
      });
      setIsAddingItem(false);
    }
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      case 'medium': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      default: return 'text-slate-600 bg-slate-50 dark:bg-slate-700 dark:text-slate-400';
    }
  };

  const categories = ['all', 'Produce', 'Meat', 'Dairy', 'Grains', 'Pantry', 'Frozen'];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${theme.colors.text.primary}`}>Grocery List Planner</h2>
          <p className={`${theme.colors.text.tertiary} mt-1`}>Organize your shopping list with smart categorization</p>
        </div>
        <button
          onClick={() => setIsAddingItem(true)}
          className={`flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors`}
        >
          <Plus className="h-4 w-4" />
          <span>Add Item</span>
        </button>
      </div>

      {/* Add Item Form */}
      {isAddingItem && (
        <div className={`${theme.colors.background.card} rounded-lg border ${theme.colors.border.primary} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${theme.colors.text.primary}`}>Add New Item</h3>
            <button
              onClick={() => setIsAddingItem(false)}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}>Item Name</label>
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.primary} ${theme.colors.background.primary} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
                placeholder="e.g., Bananas"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}>Amount</label>
              <input
                type="number"
                value={newItem.amount}
                onChange={(e) => setNewItem(prev => ({ ...prev, amount: parseInt(e.target.value) || 1 }))}
                className={`w-full px-3 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.primary} ${theme.colors.background.primary} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
                min="1"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}>Unit</label>
              <select
                value={newItem.unit}
                onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                className={`w-full px-3 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.primary} ${theme.colors.background.primary} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
              >
                <option value="piece">piece</option>
                <option value="pieces">pieces</option>
                <option value="lb">lb</option>
                <option value="lbs">lbs</option>
                <option value="bag">bag</option>
                <option value="bunch">bunch</option>
                <option value="container">container</option>
                <option value="containers">containers</option>
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}>Category</label>
              <select
                value={newItem.category}
                onChange={(e) => setNewItem(prev => ({ ...prev, category: e.target.value }))}
                className={`w-full px-3 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.primary} ${theme.colors.background.primary} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
              >
                <option value="Produce">Produce</option>
                <option value="Meat">Meat</option>
                <option value="Dairy">Dairy</option>
                <option value="Grains">Grains</option>
                <option value="Pantry">Pantry</option>
                <option value="Frozen">Frozen</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}>Priority</label>
              <select
                value={newItem.priority}
                onChange={(e) => setNewItem(prev => ({ ...prev, priority: e.target.value as 'low' | 'medium' | 'high' }))}
                className={`w-full px-3 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.primary} ${theme.colors.background.primary} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}>Notes (optional)</label>
              <input
                type="text"
                value={newItem.notes}
                onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))}
                className={`w-full px-3 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.primary} ${theme.colors.background.primary} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
                placeholder="e.g., Organic preferred"
              />
            </div>
          </div>
          
          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              onClick={() => setIsAddingItem(false)}
              className={`px-4 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.secondary} ${theme.colors.interactive.hover}`}
            >
              Cancel
            </button>
            <button
              onClick={addItem}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              <Save className="h-4 w-4 inline mr-2" />
              Add Item
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={`${theme.colors.background.card} rounded-lg border ${theme.colors.border.primary} p-4`}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.primary} ${theme.colors.background.primary} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-slate-400 dark:text-slate-500" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`px-3 py-2 border ${theme.colors.border.secondary} rounded-lg ${theme.colors.text.primary} ${theme.colors.background.primary} focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent`}
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Shopping List */}
      <div className={`${theme.colors.background.card} border ${theme.colors.border.primary} rounded-lg`}>
        <div className="p-6">
          <h3 className={`text-lg font-semibold ${theme.colors.text.primary} mb-4`}>Shopping List Items</h3>
          
          <div className="space-y-3">
            {filteredItems.map((item) => (
              <div 
                key={item.id} 
                className={`flex items-center justify-between p-4 border rounded-lg transition-all ${
                  item.is_checked 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 opacity-75' 
                    : `${theme.colors.background.primary} ${theme.colors.border.primary} hover:border-blue-300 dark:hover:border-blue-600`
                }`}
              >
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => toggleItemChecked(item.id)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      item.is_checked 
                        ? 'bg-green-600 border-green-600 text-white' 
                        : 'border-slate-300 dark:border-slate-600 hover:border-green-500 dark:hover:border-green-400'
                    }`}
                  >
                    {item.is_checked && <Check className="w-4 h-4" />}
                  </button>
                  
                  <div className="flex items-center space-x-3">
                    <div className="text-slate-400 dark:text-slate-500">
                      {item.category === 'Produce' ? <Apple className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                    </div>
                    
                    <div>
                      <div className={`font-medium ${item.is_checked ? 'line-through text-slate-500 dark:text-slate-400' : theme.colors.text.primary}`}>
                        {item.name}
                      </div>
                      <div className={`text-sm ${theme.colors.text.tertiary}`}>
                        {item.amount} {item.unit} • {item.category}
                      </div>
                      {item.notes && (
                        <div className={`text-xs ${theme.colors.text.muted} mt-1`}>
                          Note: {item.notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(item.priority)}`}>
                    {item.priority}
                  </span>
                  
                  <button
                    onClick={() => removeItem(item.id)}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600 dark:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            
            {filteredItems.length === 0 && (
              <div className="text-center py-8">
                <ShoppingCart className="h-12 w-12 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
                <p className={`${theme.colors.text.tertiary}`}>
                  {searchTerm || selectedCategory !== 'all' 
                    ? 'No items match your filters' 
                    : 'Your shopping list is empty'
                  }
                </p>
                <button
                  onClick={() => setIsAddingItem(true)}
                  className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4 inline mr-2" />
                  Add Your First Item
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      {items.length > 0 && (
        <div className={`${theme.colors.background.card} rounded-lg border ${theme.colors.border.primary} p-4`}>
          <div className="flex items-center justify-between text-sm">
            <span className={`${theme.colors.text.secondary}`}>
              {items.filter(item => item.is_checked).length} of {items.length} items completed
            </span>
            <span className={`${theme.colors.text.secondary}`}>
              {Math.round((items.filter(item => item.is_checked).length / items.length) * 100)}% complete
            </span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 mt-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(items.filter(item => item.is_checked).length / items.length) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroceryListPlanner; 
